import { config } from '@abyss/web/tools/config';
import { storage } from '@abyss/web/tools/storage';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { getMemberRelvancySpecialityRollupCodes } from '../../../../api/src/utils/specialityUtils/specialityUtils';
import {
  CareCategories,
  ChipCategories,
  Constants,
  CoverageTypesCodes,
  FilterCollectionModel,
  LAWW_PORTAL_NAME,
  OPTUM_CORRELATION_ID_HEADER,
  ProviderTypes,
  ReverseCoverageTypesCodes,
  UESSearchType,
} from '../../common/Constants';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import {
  isEniSsoMember,
  isUmrMember,
} from '../../common/Utils/memberUtils/memberUtils';
import { PortalContext } from '../../context/PortalContext';
import { SearchFilterContext } from '../../context/SearchFilterContext';
import { isMissingParameters } from '../../errors/helpers';
import { useConfigs } from '../../frontends/ProviderSearch/context/Analytics/Analytics';
import { getEnableMapPinRedesign } from '../../frontends/ProviderSearch/routes/MapView/utils';
import {
  filterAoeCodeByCoverageTypeForFilterCount,
  getUpdatedAoeCodesWithFilterOptions,
  getUpdatedUesAoeCodes,
  getUpdateduesBehavioralFacilityProgram,
  setBehavioralProgramIdForEapFilter,
} from '../../frontends/ProviderSearch/routes/SearchResults/utils';
import { useCurrentMemberIsPreEffective } from '../../hooks/useCurrentMemberIsPreEffective';
import { CustomPreferredPolicy } from '../../models/Lagoon';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { useMapStore } from '../../store/useMapStore';
import { MapState } from '../../store/useMapStore/mapStore';
import { usePCPStore } from '../../store/usePCPStore';
import { PCPSearchStore } from '../../store/usePCPStore/pcpStore';
import { useSearchStore } from '../../store/useSearchStore';
import { SearchState } from '../../store/useSearchStore/searchStore';
import { useTypeaheadStore } from '../../store/useTypeaheadStore';
import { TypeaheadState } from '../../store/useTypeaheadStore/typeaheadStore';
import { LagoonData, ternaryExpression } from '../../utils/common';
import { returnSuppressFlag } from '../../utils/featureSuppress';
import { shouldSuppressVirtualOnlyLocationIndicator } from '../../utils/indicator.utils';
import { getMapViewPageSize } from '../../utils/map.utils';
import {
  getProviderType,
  getProviderResults as parseProviderResults,
} from '../../utils/providerDetails.utils';
import {
  filterSearchResults,
  getAutoIncrementRadius,
  getBehavioralFacilityPrograms,
  getBehavioralFacilityProgramsProviderSearch,
  getBehavioralFacilityProgramsProviderSearchV2,
  getExcludeNatAncForNetworkIds,
  getFilteredBHFacilityProgramKeywordValue,
  getLob,
  getLocale,
  getMemberEffectiveDate,
  getMemberHealthCoverageType,
  getOrgTypeCodes,
  getProviderAoeCodes,
  getProviderAoeCodesV2,
  getProviderIds,
  getProviderSearchQueryPreCheckVariables,
  getProviderSearchVariables,
  getRecommendationPayload,
  getRegionalVirtualCareProviderArray,
  getRollupCodes,
  getSearchSuggestion,
  getSortByValue,
  getSpecialityRollupCodes,
  getUpdatedFilterState,
  getVirtualCareLicenseStateCodes,
  isValidPlanVariationCode,
  memberCatRPK,
  parseProviderSearchFilters,
  shouldBlockNationalProviders,
  shouldDisablePCPIndAndANP,
  shouldEnableRegionalVirtualCareSection,
  validateSourceAndDestination,
} from '../../utils/providerSearch.utils';
import {
  buildCoverageTypesArray,
  buildProviderTypesArray,
  filterSearchResultsV2,
  getSearchNameForV2,
  mapProviderTypesToRoleCodes,
} from '../../utils/providerSearchV2.utils';
import {
  getUniqueSearchString,
  resolveIsPCPLandingPage,
} from '../../utils/typeahead.utils';
import {
  getClaimOrEligibilitySystemTypeCode,
  getCoverageEffDateRange,
  getCoverageTypes,
  getCurrentMember,
  getIsCustomPreferredPolicy,
  getIsOxfordAndUSP,
  getIsPolicyIDRequired,
  getMemberPlanYear,
  getNetworkIdsForPES,
  getOnlineRetailers,
  getPlanVariationCode,
  getReciprocityId,
  getSitusState,
  isPlanVariationCodeRequired,
  memberHasBHCoverage,
} from '../../utils/user.utils';
import { useCategoryLLMFlag } from '../useCategoryLLMFlag';
import {
  useCurrentMember,
  useCurrentMemberOrDependentInfo,
} from '../useCurrentMember';
import { useCustomQuery } from '../useCustomQuery';
import { useFeatureFlag } from '../useFeatureFlag';
import { useGeoLocationStorage } from '../useGeoLocationStorage';
import { useLagoon } from '../useLagoon';
import { useMinProviderCount } from '../useMinProviderCount';
import { useSmartChoice } from '../useSmartChoice';
import {
  getTypeaheadPseudoRollup,
  useTypeaheadPseudoRollupTable,
} from '../useTypeaheadPseudoRollup/useTypeaheadPseudoRollup';
import { determineSearchRadius } from './helper';
import GET_PRINT_PDF_PROVIDERS from './PrintPdfProviderSearch.graphql';
import GET_PROVIDER_SEARCH_QUERY from './ProviderSearch.graphql';
import GET_PROVIDER_SEARCH_PINS from './ProviderSearchPins.graphql';
import GET_PROVIDER_SEARCH_V2_QUERY from './ProviderSearchV2.graphql';

export interface ProviderSearchParams {
  pcpIndicator: boolean | null;
  providerType: string;
  providerQueryTypes: any[];
  searchType: string;
  coverageType: string;
  virtualCare: boolean;
  searchRadius: number;
  isExtendedSearchEnabled: boolean;
  preSelectEapFilter: boolean;
  conditionAoeCodes: string[];
  aoeCodes?: string[];
  includeSpecialityRollupCodes: string[];
  behavioralFacilityPrograms: string;
  eligibilitySystemTypeCode: string;
  claimSystemTypeCode: string;
  planVariationCode: string;
  typeaheadAoeCodes: string[];
  isUserTextSearch: boolean;
  keyword: string;
  shouldGetHGData: boolean;
  autoIncrementRadius: string[];
  behavioralProgramId: string;
  includeAdditionalFieldsForPrint: boolean;
  mapLocationsOnly: boolean;
  extendedPinsDrawerReq: boolean;
  providerLocationId: string;
  search: string;
  typeaheadSearch: boolean;
}
const shouldRunQuery = (isDeeplink, isDeeplinkAutoSearchRadiusQueryDone) =>
  isDeeplink && !isDeeplinkAutoSearchRadiusQueryDone;

/**
 * @deprecated
 * This hook is deprecated. Please use `useProviderSearchV2` instead.
 */
export const useProviderSearch = (options) => {
  const [filteredResult, setFilteredResult] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [headers, setHeaders] = useState({});
  const [queryResult, queryAction] = useCustomQuery(GET_PROVIDER_SEARCH_QUERY, {
    ...options,
    url: config('GRAPHQL_API_URL'),
    accessor: 'providerSearch',
    onCalled: () => {
      setIsLoading(true);
    },
    /* istanbul ignore next */
    onCompleted: (result) => {
      const data = filterSearchResults(result?.data);
      const headers = {
        correlationId: result.headers[OPTUM_CORRELATION_ID_HEADER],
      };
      if (options?.onCompleted) {
        options.onCompleted({ ...data, headers });
      }
      setFilteredResult(data);
      setIsLoading(false);
      setHeaders(headers);
    },
    onError: () => {
      setIsLoading(false);
    },
  });

  const parsedResult = {
    ...queryResult,
    isLoading,
    data: filteredResult,
    headers,
  };

  return [parsedResult, queryAction];
};

export const useProviderSearchV2 = (options) => {
  const [, setCorrelationIDSessionStorage] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.CORRELATION_ID_HEADER,
    ''
  );
  const [queryResult, queryAction] = useCustomQuery(GET_PROVIDER_SEARCH_QUERY, {
    ...options,
    url: config('GRAPHQL_API_URL'),
    accessor: 'providerSearch',
    onCompleted: (result) => {
      const data = filterSearchResults(result?.data);
      const headers = {
        correlationId: result.headers[OPTUM_CORRELATION_ID_HEADER],
      };
      result?.headers[OPTUM_CORRELATION_ID_HEADER] &&
        setCorrelationIDSessionStorage(
          result?.headers[OPTUM_CORRELATION_ID_HEADER]
        );
      if (options?.onCompleted) {
        options.onCompleted({ ...data, headers });
      }
    },
  });

  const parsedResult = useMemo(() => {
    const isLoading = queryResult.loading;
    const data = queryResult.data ? filterSearchResults(queryResult.data) : {};
    const headers = queryResult.headers
      ? { correlationId: queryResult.headers[OPTUM_CORRELATION_ID_HEADER] }
      : {};

    return {
      ...queryResult,
      isLoading,
      data,
      headers,
    };
  }, [
    JSON.stringify(queryResult.loading),
    JSON.stringify(queryResult.data),
    JSON.stringify(queryResult.headers),
    JSON.stringify(options),
  ]);

  return [parsedResult, queryAction];
};

export const useProviderSearchPins = (options) => {
  const [, setCorrelationIDSessionStorage] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.CORRELATION_ID_HEADER,
    ''
  );
  const [queryResult, queryAction] = useCustomQuery(GET_PROVIDER_SEARCH_PINS, {
    ...options,
    url: config('GRAPHQL_API_URL'),
    accessor: 'providerSearchPins',
    onCompleted: (result) => {
      const data = filterSearchResults(result?.data);
      const headers = {
        correlationId: result.headers[OPTUM_CORRELATION_ID_HEADER],
      };
      result?.headers[OPTUM_CORRELATION_ID_HEADER] &&
        setCorrelationIDSessionStorage(
          result?.headers[OPTUM_CORRELATION_ID_HEADER]
        );
      if (options?.onCompleted) {
        options.onCompleted({ ...data, headers });
      }
    },
  });

  const parsedResult = useMemo(() => {
    const isLoading = queryResult.loading;
    const data = queryResult.data ? filterSearchResults(queryResult.data) : {};
    const headers = queryResult.headers
      ? { correlationId: queryResult.headers[OPTUM_CORRELATION_ID_HEADER] }
      : {};

    return {
      ...queryResult,
      isLoading,
      data,
      headers,
    };
  }, [
    JSON.stringify(queryResult.loading),
    JSON.stringify(queryResult.data),
    JSON.stringify(queryResult.headers),
    JSON.stringify(options),
  ]);

  return [parsedResult, queryAction];
};

export const usePrintPdfProviderSearch = (options) => {
  const [, setCorrelationIDSessionStorage] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.CORRELATION_ID_HEADER,
    ''
  );
  const [queryResult, queryAction] = useCustomQuery(GET_PRINT_PDF_PROVIDERS, {
    url: config('GRAPHQL_API_URL'),
    accessor: 'printPdfProviderSearch',
    ...options,
    onCompleted: (result) => {
      const rawData = result?.data;
      const headers = {
        correlationId: result.headers[OPTUM_CORRELATION_ID_HEADER],
      };
      result?.headers[OPTUM_CORRELATION_ID_HEADER] &&
        setCorrelationIDSessionStorage(
          result?.headers[OPTUM_CORRELATION_ID_HEADER]
        );
      if (options?.onCompleted) {
        options.onCompleted({ ...rawData, headers });
      }
    },
  });

  return [queryResult, queryAction];
};

function getDisablePCPIndAndANP(
  currentMember,
  disablePCPIndAndANPFlag,
  disablePCPIndAndANPEarlyPayFlag
) {
  if (!currentMember) return disablePCPIndAndANPFlag;
  return currentMember.isIFPEarlyPay
    ? disablePCPIndAndANPEarlyPayFlag
    : disablePCPIndAndANPFlag;
}

function getChipCategory(chipValue) {
  return chipValue === CareCategories.PRIMARY_CARE
    ? ChipCategories.PRIMARY_CARE
    : '';
}

export const useProviderSearchVariables = (params) => {
  const { portalData } = useContext(PortalContext);
  const { searchFilters } = useContext(SearchFilterContext);
  const [urlParams] = useSearchParams();
  const isSmartChoice = useSmartChoice();
  const isPreEffective = useCurrentMemberIsPreEffective();
  const { longitude, latitude, stateCode } = useGeoLocationStorage();
  const pseudoRollupCodeTable: LagoonData[] = useTypeaheadPseudoRollupTable();

  const { setDefaultSearchFilters, defaultSearchFilters } =
    useContext(SearchFilterContext);
  const [resetFilters, setResetFilters] = useState<Boolean>(false);
  const [resetFiltersVariables, setResetFiltersVariables] = useState({});
  const allConfigs = useLagoon('config')();

  const [
    useMedicalNewRollUpCodes,
    useBHNewRollUpCodes,
    useVisionNewRollUpCodes,
    useDentalNewRollUpCodes,
    uspTierOneFlag,
    uspToggleFlag,
    disablePCPIndAndANPFlag,
    disablePCPIndAndANPEarlyPayFlag,
    eapBHProgramFlag,
    isFutureTerminationDemo,
    enableMockProviderIdsForPreEffective,
    enableTierOneMockProviderIds,
    enableMapViewLargeResults,
    enableMapPinRedesignFlag,
    oxfordPlnFlag,
    enableVirtualCareEpic,
    enableClaimsBasedLlmAllLanguages,
    enableCategoryCountV2,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.USE_MEDICAL_NEW_ROLLUP_CODES,
    ConstantsLagoon.FEATURE_FLAGS.USE_BH_NEW_ROLLUP_CODES,
    ConstantsLagoon.FEATURE_FLAGS.USE_VISION_NEW_ROLLUP_CODES,
    ConstantsLagoon.FEATURE_FLAGS.USE_DENTAL_NEW_ROLLUP_CODES,
    ConstantsLagoon.FEATURE_FLAGS.USP_TIER1_ENABLE,
    ConstantsLagoon.FEATURE_FLAGS.USP_ENABLE,
    ConstantsLagoon.FEATURE_FLAGS.DISABLE_PCP_IND_AND_ANP,
    ConstantsLagoon.FEATURE_FLAGS.DISABLE_PCP_IND_AND_ANP_EARLYPAY,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_EAP_FILTER_USING_BH_PROGRAM,
    ConstantsLagoon.FEATURE_FLAGS.FUTURE_TERMINATION_DEMO,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MOCK_PROVIDER_IDS_FOR_PRE_EFFECTIVE,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_TIER_ONE_MOCK_PROVIDER_IDS,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MAP_VIEW_LARGE_RESULTS,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MAP_PIN_REDESIGN,
    ConstantsLagoon.FEATURE_FLAGS.OXFORD_PLN,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_VIRTUAL_CARE_EPIC,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_CLAIMS_BASED_LLM_ALL_LANGUAGES,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_CATEGORY_COUNT_V2,
  ]);

  const [
    mapViewPageSizeConfig,
    terminationProviderIdsDemo,
    futureStartDateProviderIds,
    tierOneMockProviderIds,
    acoMockProviderIds,
    premiumCareMockProviderIds,
    mnrFutureMockProviderIds,
    excludeNatAncForNetworkIds,
    autoIncrementRadiusFromConfig,
  ] = useConfigs([
    ConstantsLagoon.CONFIG.MAP_VIEW_PAGE_SIZE,
    'TERMINATION_PROVIDER_IDS_DEMO',
    'FUTURE_START_PROVIDERIDS',
    'TIER1_MOCK_PROVIDERIDS',
    'ACO_MOCK_PROVIDERIDS',
    'PREMIUM_MOCK_PROVIDERIDS',
    'MNR_MOCK_PROVIDERIDS',
    'EXCLUDE_NAT_ANC_FOR_NETWORK_IDS',
    'AUTO_INCREMENT_SEARCH_RADIUS',
  ]);

  const isVirtualCareUnetRailroadSuppressed = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.VIRTUAL_CARE_UNET_RAILROAD
  );

  const {
    coverageType: coverageTypeFromStore,
    storeLob,
    chipValue: chipValueFromStore,
  } = useChipStore(
    useShallow((state: ChipState) => ({
      coverageType: state.coverageType,
      storeLob: state.lob,
      chipValue: state.chipValue,
    }))
  );

  const {
    pcpIndicator = false,
    dependentSeqNbr,
    choosePCP = false,
  } = usePCPStore(
    useShallow((state: PCPSearchStore) => ({
      pcpIndicator: state.pcpSearchState.pcpIndicator,
      dependentSeqNbr: state.pcpSearchState.dependentSeqNbr,
      choosePCP: state.pcpSearchState.choosePCP,
    }))
  );

  const {
    includeOrgTypeCodes,
    excludeOrgTypeCodes,
    requestType = '',
    onlineRetailers,
    behavioralFacilityPrograms,
  } = useSearchStore(
    useShallow((state: SearchState) => ({
      includeOrgTypeCodes: state.searchStore.includeOrgTypeCodes,
      excludeOrgTypeCodes: state.searchStore.excludeOrgTypeCodes,
      requestType: state.searchStore.requestType,
      onlineRetailers: state.searchStore.onlineRetailers,
      behavioralFacilityPrograms: state.searchStore.behavioralFacilityPrograms,
    }))
  );

  const { mapLatitude, mapLongitude, mapSearchRadius } = useMapStore(
    useShallow((state: MapState) => ({
      mapLatitude: state.mapLatitude,
      mapLongitude: state.mapLongitude,
      mapSearchRadius: state.mapSearchRadius,
    }))
  );

  const {
    search: searchFromStore = '',
    providerType: stateProviderType = '',
    category,
    searchType: searchTypeFromStore,
    sectionType: stateSectionType = '',
    specialtyCode,
    includeSpecialityRollupCodes,
    aoeCodes: typeaheadAoeCodes,
    aoeCodefromUES,
    behavioralFacilityProgramfromUES,
    providerTypeFromTypeAhead,
    acceptingNewPatients: acceptingNewPatientsFromStore,
    keyword: keywordFromStore = '',
    isUserTextSearch,
    pseudoVerticals = '',
    suggestion,
    bhProgramFacilityAgg,
    platinumProgramCode,
    providerQueryTypes,
    preferredSpecialityCode,
    searchFromTypeAhead,
    setTypeaheadSearchStore,
    isTypeaheadStoreValuesSet,
    virtualCare,
    vertical,
  } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      search: state.typeaheadSearchStore.search,
      providerType: state.typeaheadSearchStore.providerType,
      category: state.typeaheadSearchStore.category,
      searchType: state.typeaheadSearchStore.searchType,
      sectionType: state.typeaheadSearchStore.sectionType,
      specialtyCode: state.typeaheadSearchStore.specialtyCode,
      includeSpecialityRollupCodes:
        state.typeaheadSearchStore.includeSpecialityRollupCodes,
      aoeCodes: state.typeaheadSearchStore.aoeCodes,
      aoeCodefromUES: state.typeaheadSearchStore.aoeCodes,
      behavioralFacilityProgramfromUES:
        state.typeaheadSearchStore.bhProgramFacility,
      providerTypeFromTypeAhead: state.typeaheadSearchStore.providerType,
      acceptingNewPatients: state.typeaheadSearchStore.acceptingNewPatients,
      keyword: state.typeaheadSearchStore.keyword,
      isUserTextSearch: state.typeaheadSearchStore.isUserTextSearch,
      pseudoVerticals: state.typeaheadSearchStore.pseudoVerticals,
      preferredSpecialityCode:
        state.typeaheadSearchStore.preferredSpecialityCode,
      suggestion: state.typeaheadSearchStore.suggestion,
      bhProgramFacilityAgg: state.typeaheadSearchStore.bhProgramFacilityAgg,
      platinumProgramCode: state.typeaheadSearchStore.platinumProgramCode,
      providerQueryTypes: state.typeaheadSearchStore.providerQueryTypes,
      searchFromTypeAhead: state.typeaheadSearchStore.searchFromTypeAhead,
      isTypeaheadStoreValuesSet:
        state.typeaheadSearchStore.isTypeaheadStoreValuesSet,
      setTypeaheadSearchStore: state.setTypeaheadSearchStore,
      virtualCare: state.typeaheadSearchStore.virtualCare,
      vertical: state.typeaheadSearchStore.vertical,
    }))
  );

  const uesAoeCodes = getUpdatedUesAoeCodes(
    aoeCodefromUES,
    providerTypeFromTypeAhead
  );
  const uesBehavioralFacilityProgram = getUpdateduesBehavioralFacilityProgram(
    behavioralFacilityProgramfromUES,
    providerTypeFromTypeAhead
  );

  const currentMember = useCurrentMemberOrDependentInfo(dependentSeqNbr);
  const featureFlags: [{ key: string; active: boolean }] =
    useLagoon('feature-flags')();
  const customPreferredPolicies: CustomPreferredPolicy[] = useLagoon(
    'custom-preferred-policies'
  )();
  const rollupCodeMapLagoonData = useLagoon(
    Constants.LAGOON_TABLE.ROLLUPCODE_MAPPING
  )();
  const tierBenefits = useLagoon('tier-benefit')();

  const [deeplinkSearchParams] = useSessionStorage<{
    includeSpecialityRollupCodes: string;
  }>(Constants.STORAGE_KEYS.SESSION.PSX_DEEPLINK_PARAMS, {
    includeSpecialityRollupCodes: '',
  });
  const [practitionerParams] = useSessionStorage<any>(
    `${Constants.STORAGE_KEYS.SESSION.MIXED_RESULTS_TYPE_AHEAD_PARAMS}_${ProviderTypes.PRACTITIONER}`,
    {}
  );
  const [medicalParams] = useSessionStorage<any>(
    `${Constants.STORAGE_KEYS.SESSION.MIXED_RESULTS_TYPE_AHEAD_PARAMS}_${ProviderTypes.MEDICAL_GROUP}`,
    {}
  );
  const [orgParams] = useSessionStorage<any>(
    `${Constants.STORAGE_KEYS.SESSION.MIXED_RESULTS_TYPE_AHEAD_PARAMS}_${ProviderTypes.ORGANIZATION}`,
    {}
  );

  const searchParamsObject = Object.fromEntries(urlParams.entries());
  const {
    search: searchFromParams,
    searchSuggestionEN,
    searchType: searchTypeFromParams,
    chipValue: chipValueFromParams,
    coverageType: coverageTypeFromParams,
    sectionType: sectionTypeFromParams,
    [Constants.SOURCE]: sourceFromGlobalSearch,
    [Constants.PSX_SOURCE]: psxSource,
  } = searchParamsObject;
  const isDeeplink = urlParams.get('isDeepLink') === 'true';
  const zipcodeParam = urlParams.get('zipcode');
  const isDeeplinkAutoSearchRadiusQueryDone =
    urlParams.get('isDeeplinkAutoSearchRadiusQueryDone') == 'true';

  const isDeeplinkSearch = sourceFromGlobalSearch === Constants.PSX_ROUTER;
  const rollupCodes = useMemo(() => {
    if (isDeeplinkSearch) {
      const includeSpecialityRollupCodesFromDeeplink =
        deeplinkSearchParams?.includeSpecialityRollupCodes;
      setTypeaheadSearchStore({
        includeSpecialityRollupCodes: includeSpecialityRollupCodesFromDeeplink,
        specialtyCode: '',
      });
      return includeSpecialityRollupCodesFromDeeplink.split(',');
    }
    const codes = getSpecialityRollupCodes(
      specialtyCode,
      includeSpecialityRollupCodes,
      category
    );
    return codes ? codes.split(',') : [];
  }, [specialtyCode, includeSpecialityRollupCodes, category, isDeeplinkSearch]);

  const {
    ddpCode,
    demographics,
    isIFPEarlyPay,
    lineOfBusiness: memberLob,
    population,
    policyNumber: policyID,
  } = currentMember;
  const dateOfBirth = demographics?.dateOfBirth;
  const gender = demographics?.gender;
  const sectionType = sectionTypeFromParams || stateSectionType;
  const chipValue = chipValueFromParams || chipValueFromStore;
  const coverageType = coverageTypeFromParams || coverageTypeFromStore;
  const providerType = getProviderType(stateProviderType, sectionType);

  const search =
    getSearchSuggestion(enableClaimsBasedLlmAllLanguages, searchSuggestionEN) ||
    searchFromParams ||
    searchFromStore ||
    '';
  const keyword =
    getSearchSuggestion(enableClaimsBasedLlmAllLanguages, searchSuggestionEN) ||
    keywordFromStore ||
    searchFromParams;
  const searchType = searchTypeFromParams || searchTypeFromStore;
  const isPCPLandingPage = resolveIsPCPLandingPage(
    chipValue,
    psxSource,
    sourceFromGlobalSearch
  );
  const regionalProviderArray = getRegionalVirtualCareProviderArray(
    chipValue,
    allConfigs
  );
  const virtualCareLicenseStateCode = getVirtualCareLicenseStateCodes(
    regionalProviderArray
  );
  const enableRegionalVirtualCareSection =
    shouldEnableRegionalVirtualCareSection(
      enableVirtualCareEpic,
      isVirtualCareUnetRailroadSuppressed,
      regionalProviderArray
    );

  const disablePCPIndAndANP = getDisablePCPIndAndANP(
    currentMember,
    disablePCPIndAndANPFlag,
    disablePCPIndAndANPEarlyPayFlag
  );
  function getAcceptingNewPatients(
    disablePCPIndAndANP: boolean,
    isPCPLandingPage: boolean,
    choosePCP: boolean,
    acceptingNewPatientsFromStore: boolean | undefined
  ) {
    return shouldDisablePCPIndAndANP(
      disablePCPIndAndANP,
      isPCPLandingPage,
      choosePCP
    )
      ? undefined
      : acceptingNewPatientsFromStore;
  }

  const acceptingNewPatients = getAcceptingNewPatients(
    disablePCPIndAndANP,
    isPCPLandingPage,
    choosePCP,
    acceptingNewPatientsFromStore
  );

  const sortByValue = getSortByValue(isSmartChoice);
  const coverages = getCoverageTypes(currentMember);
  const planVariationCode = getPlanVariationCode(currentMember, coverageType);

  const isUmrPopulation = isUmrMember(portalData?.portalName);
  const medicalCoverage = getMemberHealthCoverageType(
    currentMember?.eligibility,
    CoverageTypesCodes.MEDICAL
  );
  const productCode = medicalCoverage?.productCode;
  const marketType = medicalCoverage?.marketType;
  const [searchParamsList] = useSearchParams();

  const llmValue = useCategoryLLMFlag(providerType, chipValue);
  const invokeLlmModel = ternaryExpression(
    searchParamsList.get('fromLandingPage') === 'true',
    false,
    llmValue
  );
  const isGuestUser = portalData?.portalConfig?.isGuestUser;
  const isPolicyIDRequired = getIsPolicyIDRequired(currentMember, isGuestUser);
  const autoIncrementRadius = getAutoIncrementRadius(
    autoIncrementRadiusFromConfig
  );
  const pageSizeDefault = getMapViewPageSize(
    enableMapViewLargeResults,
    mapViewPageSizeConfig
  );
  const { isOXMember } = getIsOxfordAndUSP(
    currentMember,
    coverageType,
    featureFlags
  );
  const pseudoVerticalsDetails = getTypeaheadPseudoRollup(
    pseudoRollupCodeTable,
    pseudoVerticals,
    suggestion || '',
    isOXMember,
    oxfordPlnFlag
  );
  const forceMapPinRedesign = storage.session.get(
    Constants.STORAGE_KEYS.SESSION.FORCE_MAP_PIN_REDESIGN
  );
  const enableMapPinRedesign = getEnableMapPinRedesign(
    enableMapPinRedesignFlag,
    forceMapPinRedesign
  );

  const isPseudoRollupSearch = pseudoVerticalsDetails?.isPseudoRollupSearch;
  const rollupCodeMap = rollupCodeMapLagoonData?.reduce((acc, item) => {
    if (validateSourceAndDestination(item.source, item.destination)) {
      acc[item.source] = item.destination;
    }
    return acc;
  }, {});

  const reciprocityId = getNetworkIdsForPES(
    currentMember,
    coverageType,
    featureFlags
  );
  const isCustomPreferredPolicy: boolean = getIsCustomPreferredPolicy(
    customPreferredPolicies,
    policyID,
    reciprocityId
  );
  const { membershipCategory, rulesPackageKey } = memberCatRPK(
    currentMember,
    uspTierOneFlag,
    uspToggleFlag,
    tierBenefits,
    providerType
  );
  const bhFacilityPrograms = enableCategoryCountV2
    ? getBehavioralFacilityProgramsProviderSearchV2({
        searchType,
        providerType,
        bhProgramFacilityAgg,
        providerQueryTypes,
      })
    : getBehavioralFacilityProgramsProviderSearch({
        searchType,
        providerType,
        bhProgramFacilityAgg,
        providerQueryTypes,
      });
  const typeaheadAoeCodesForEachProvider = enableCategoryCountV2
    ? getProviderAoeCodesV2({
        typeaheadAoeCodes,
        providerQueryTypes,
        providerType,
        searchType,
      })
    : getProviderAoeCodes({
        typeaheadAoeCodes,
        providerQueryTypes,
        providerType,
        searchType,
      });
  const filteredAoeCodesForEachProvider =
    filterAoeCodeByCoverageTypeForFilterCount(
      coverageType,
      typeaheadAoeCodesForEachProvider
    );
  const currentMemberEffectiveDate = getCoverageEffDateRange(
    currentMember,
    coverageType
  ).startDate;
  const specialityRollupCodes = getRollupCodes(
    bhFacilityPrograms,
    rollupCodes,
    filteredAoeCodesForEachProvider,
    enableCategoryCountV2,
    vertical
  )?.filter((code) => code !== '');

  let recommendationPreferences = {};
  if (specialityRollupCodes.length > 0) {
    recommendationPreferences = getRecommendationPayload(specialityRollupCodes);
  } else {
    const memberRelevancyCodes = getMemberRelvancySpecialityRollupCodes({
      memberDemographics: {
        dateOfBirth: demographics?.dateOfBirth,
        gender: demographics?.gender,
      },
      providerType,
      lob: getLob(memberLob, storeLob),
      population,
      useNewAgeAndGenderRollups: true,
      pcpIndicator: Boolean(demographics?.primaryCare?.providerID),
    });
    recommendationPreferences = getRecommendationPayload(memberRelevancyCodes);
  }
  let firstPageVariables;

  switch (providerType) {
    case ProviderTypes.ORGANIZATION:
      firstPageVariables = orgParams;
      break;
    case ProviderTypes.MEDICAL_GROUP:
      firstPageVariables = medicalParams;
      break;
    default:
      firstPageVariables = practitionerParams;
      break;
  }

  const {
    pageNumber = 1,
    pageSize = pageSizeDefault,
    selectedFilters = {},
    shouldGetHGData,
    providerId,
    extendedPinsDrawerReq,
    providerLocationId,
    initialPage = false,
    mapLocationsOnly = false,
    includeAdditionalFieldsForPrint = false,
  } = params;

  const filterParams = parseProviderSearchFilters(
    selectedFilters,
    coverageType
  );
  const chipCategory = getChipCategory(chipValue);
  const initialFilterParamsRadius = filterParams?.searchRadius;

  const behavioralFacilityProgramsValue = enableCategoryCountV2
    ? ''
    : searchFromTypeAhead
    ? getFilteredBHFacilityProgramKeywordValue(bhFacilityPrograms, filterParams)
    : getBehavioralFacilityPrograms(filterParams, behavioralFacilityPrograms);

  const memberPolicyNumber = currentMember?.policyNumber;
  const memberCoverages = getCoverageTypes(currentMember);
  const hasBH = memberHasBHCoverage(memberCoverages);
  const featureSuppressionData = useLagoon(
    Constants.LAGOON_TABLE.FEATURE_SUPPRESSION
  )();
  const virtualOnlyLocationIndFlagKey =
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS
      .VIRTUAL_ONLY_LOCATION_INDICATOR_UNET_RAILROAD;
  const virtualOnlyLocationInd = shouldSuppressVirtualOnlyLocationIndicator(
    memberPolicyNumber,
    hasBH,
    virtualOnlyLocationIndFlagKey,
    featureSuppressionData
  );

  const uniqueSearch = getUniqueSearchString({
    search,
    psxSource,
    latitude,
    longitude,
  });
  const minimumProvidersReturn = useMinProviderCount(providerType);
  const variables = {
    lob: getLob(memberLob, storeLob),
    population,
    membershipCategory,
    coverages,
    planYear: getMemberPlanYear(isPreEffective, currentMemberEffectiveDate),
    situsState: getSitusState(currentMember, getLob(memberLob, storeLob)),
    memberEffectiveDate: getMemberEffectiveDate(
      isPreEffective,
      currentMemberEffectiveDate
    ),
    planVariationCode,
    claimSystemTypeCode: getClaimOrEligibilitySystemTypeCode(
      currentMember,
      coverageType
    )?.claimSystemTypeCode,
    eligibilitySystemTypeCode: getClaimOrEligibilitySystemTypeCode(
      currentMember,
      coverageType
    )?.eligibilitySystemTypeCode,
    policyID,
    preferredSpecialityCode,
    reciprocityId: getReciprocityId(
      filterParams,
      currentMember,
      coverageType,
      featureFlags
    ),
    rulesPackageKey,
    rollupCodeMap,
    snpType:
      coverageType == ReverseCoverageTypesCodes.MEDICAL
        ? medicalCoverage?.snpType
        : '',
    locale: getLocale(),
    shouldGetHGData,
    latitude: mapLatitude || latitude,
    longitude: mapLongitude || longitude,
    stateCode,
    memberDemographics: {
      gender,
      dateOfBirth,
    },
    coverageType,
    providerType,
    search,
    uniqueSearch,
    includeSpecialityRollupCodes: specialityRollupCodes,
    requestType,
    searchType,
    pcpIndicator: shouldDisablePCPIndAndANP(
      disablePCPIndAndANP,
      isPCPLandingPage || false,
      choosePCP
    )
      ? null
      : pcpIndicator || null,
    chipCategory,
    isPCPLandingPage,
    useNewAgeAndGenderRollups: disablePCPIndAndANP,
    invokeLlmModel: isPseudoRollupSearch ? false : invokeLlmModel, //
    keyword,
    onlineRetailers: getOnlineRetailers(onlineRetailers),
    isFutureTerminationDemo,
    terminationProviderIdsDemo,
    enableMockProviderIdsForPreEffective,
    enableTierOneMockProviderIds: enableTierOneMockProviderIds || false,
    futureStartDateProviderIds: getProviderIds(futureStartDateProviderIds),
    tierOneMockProviderIds: getProviderIds(tierOneMockProviderIds),
    acoMockProviderIds: getProviderIds(acoMockProviderIds),
    premiumCareMockProviderIds: getProviderIds(premiumCareMockProviderIds),
    mnrFutureMockProviderIds: getProviderIds(mnrFutureMockProviderIds),
    useMedicalNewRollUpCodes,
    useBHNewRollUpCodes,
    useVisionNewRollUpCodes,
    useDentalNewRollUpCodes,
    ddpCode,
    isCustomPreferredPolicy,
    cesProductCode: productCode,
    memberMarketType: marketType,
    behavioralFacilityPrograms: searchFromTypeAhead
      ? bhFacilityPrograms
      : behavioralFacilityPrograms,
    isUserTextSearch,
    minimumProvidersReturn: minimumProvidersReturn || undefined,
    excludeNatAncForNetworkIds: getExcludeNatAncForNetworkIds(
      excludeNatAncForNetworkIds
    ),
    accreditation:
      selectedFilters?.AccreditationData?.map((item) => item.value) || [],
    sortBy: sortByValue,
    ...(isSmartChoice && {
      recommendationPreferences,
      isProviderRecommendationsEnabled: isSmartChoice,
    }),
    mapLocationsOnly,
    includeAdditionalFieldsForPrint,
    isIFPEarlyPay,
    isGuestUser,
    ...(providerId && { providerId }),
    ...(providerLocationId && { providerLocationId }),
    ...(extendedPinsDrawerReq &&
      !filterParams?.virtualCare && { extendedPinsDrawerReq }),
    virtualOnlyLocationInd,
    ...(enableRegionalVirtualCareSection && {
      virtualOnlyCare: regionalProviderArray,
      virtualCareLicenseStateCode,
    }),
    ...(enableCategoryCountV2 && {
      uesAoeCode: uesAoeCodes,
      uesBehavioralFacilityProgram,
    }),
    shouldBlockNationalProviders: shouldBlockNationalProviders(
      chipValue,
      enableVirtualCareEpic,
      isVirtualCareUnetRailroadSuppressed,
      allConfigs
    ),
    virtualCare,
    platinumProgramCode,
    ...([
      UESSearchType.PhoneNumber,
      UESSearchType.NpiNumber,
      UESSearchType.LicenseNumber,
    ].includes(searchType as UESSearchType) && {
      [searchType as string]: search,
    }),
  };
  const aoeCodes = filterParams.aoeCategoriesCode
    ? [] // BH grouped AOE filter active — send empty aoeCodes; aoeCategoriesCode is passed separately
    : getUpdatedAoeCodesWithFilterOptions(
        enableCategoryCountV2 ? [] : filteredAoeCodesForEachProvider,
        filterParams.aoeCodes,
        eapBHProgramFlag
      );
  const behavioralProgramId = setBehavioralProgramIdForEapFilter(
    eapBHProgramFlag,
    selectedFilters.EapOffered
  );

  const behavioralFacilityProgramsFromValues = Array.from(
    new Set([
      ...behavioralFacilityProgramsValue
        .split(',')
        .map((program) => program.trim()),
      ...(filterParams?.behavioralFacilityPrograms
        ?.split(',')
        .map((program) => program.trim()) || []),
    ])
  ).join(',');

  const queryParams = {
    pageNumber,
    pageSize,
    includeOrgTypeCodes: getOrgTypeCodes(includeOrgTypeCodes),
    excludeOrgTypeCodes: getOrgTypeCodes(excludeOrgTypeCodes),
    searchRadius: filterParams?.searchRadius,
    sortBy: sortByValue,
    ...(isSmartChoice && {
      recommendationPreferences: {
        ...recommendationPreferences,
        recommendationRadius: filterParams?.virtualCare
          ? Number(autoIncrementRadius?.[autoIncrementRadius?.length - 1])
          : initialFilterParamsRadius,
      },
      isProviderRecommendationsEnabled: isSmartChoice,
    }),
    behavioralFacilityPrograms: behavioralFacilityProgramsFromValues,
    behavioralProgramId,
    ...filterParams,
    aoeCodes,
    agesAoeCodes: filterParams?.agesAoeCodes || [],
  };
  const { AutoSearchRadius: autoSearchRadius } = searchFilters;
  queryParams.searchRadius = determineSearchRadius(
    filterParams,
    queryParams.searchRadius,
    autoSearchRadius
  );
  const [defaultVariables, setDefaultVariables] = useState(variables);

  const isEniSsoMemberPopulation = isEniSsoMember(portalData?.portalName);

  const validPlanVariationCode = isValidPlanVariationCode(
    variables.eligibilitySystemTypeCode,
    variables.claimSystemTypeCode,
    variables.planVariationCode,
    variables.lob,
    variables.population,
    isUmrPopulation,
    isIFPEarlyPay,
    isGuestUser,
    isEniSsoMemberPopulation
  );
  const planVariationCodeRequired = isPlanVariationCodeRequired(
    memberLob,
    population,
    portalData?.portalName,
    isIFPEarlyPay,
    isGuestUser
  );

  const requiredParamsForProviderSearchQuery = {
    coverages,
    reciprocityId,
    ...(isPolicyIDRequired ? { policyID } : {}),
    latitude,
    longitude,
    coverageType,
    providerType,
    ...(planVariationCodeRequired ? { planVariationCode } : {}),
  };

  const { updatedParams } = getUpdatedFilterState(
    includeOrgTypeCodes,
    excludeOrgTypeCodes,
    acceptingNewPatients,
    validPlanVariationCode,
    sortByValue,
    mapLocationsOnly ? pageSize : pageSizeDefault
  );

  const providerSearchVariables = getProviderSearchVariables({
    resetFilters,
    defaultSearchFilters,
    selectedFilters,
    pageNumber,
    initialPage,
    firstPageVariables,
    resetFiltersVariables,
    variables: {
      ...variables,
      ...queryParams,
    },
  });

  const providerSearchVariablesForAutoSearchRadiusQuery = {
    ...providerSearchVariables,
    ...updatedParams,
    autoIncrementRadius,
    searchRadius: mapSearchRadius || updatedParams.searchRadius,
  };

  const skipQuery =
    !isPortalnameLawwAndhasZipcodeParam(portalData, zipcodeParam) &&
    (!isTypeaheadStoreValuesSet ||
      !!params.skip ||
      !filterParams.sortBy ||
      shouldRunQuery(isDeeplink, isDeeplinkAutoSearchRadiusQueryDone));
  const {
    canMakeAutoSearchRadiusProviderSearchQuery,
    canMakeProviderSearchQuery,
  } = getProviderSearchQueryPreCheckVariables({
    defaultVariables,
    variables,
    initialPage,
    isMissingParameters,
    requiredParams: requiredParamsForProviderSearchQuery,
    mapLocationsOnly,
    enableMapPinRedesign,
    isSmartChoice,
    queryParams,
    providerType,
    skipQuery,
  });

  const requiredParamsForProviderFilterCountQuery = {
    coverages,
    reciprocityId,
    ...(isPolicyIDRequired ? { policyID } : {}),
    latitude: mapLatitude || latitude,
    longitude: mapLongitude || longitude,
    coverageType,
    ...(planVariationCodeRequired ? { planVariationCode } : {}),
  };

  return {
    canMakeAutoSearchRadiusProviderSearchQuery,
    providerSearchVariablesForAutoSearchRadiusQuery,
    setResetFiltersVariables,
    setDefaultVariables,
    setDefaultSearchFilters,
    setResetFilters,
    providerSearchVariables,
    canMakeProviderSearchQuery,
    sectionType,
    acceptingNewPatients,
    requiredParamsForProviderFilterCountQuery,
    providerType,
    validPlanVariationCode,
    variables,
    featureFlags,
    psxSource,
    isDeeplink,
    isDeeplinkAutoSearchRadiusQueryDone,
    recommendationPreferences: queryParams.recommendationPreferences,
  };
};

const isPortalnameLawwAndhasZipcodeParam = (portalData, zipcodeParam) =>
  portalData?.portalName === LAWW_PORTAL_NAME && zipcodeParam;

const shouldUseProviderSearchPins = (isMapLocationsOnly) =>
  isMapLocationsOnly ? useProviderSearchPins({}) : useProviderSearchV2({});

export const useFilterProviderResults = (params) => {
  const isMapLocationsOnly = params?.mapLocationsOnly || false;
  const [, setUrlParams] = useSearchParams();
  const { searchFilters } = useContext(SearchFilterContext);
  const isFirstRender = useRef(true);
  const [
    updateAutoSearchRadiusQueryStatus,
    setUpdateAutoSearchRadiusQueryStatus,
  ] = useState(false);
  const [practitionersResult, getPractitionersResult] =
    shouldUseProviderSearchPins(isMapLocationsOnly);
  const {
    canMakeAutoSearchRadiusProviderSearchQuery,
    providerSearchVariablesForAutoSearchRadiusQuery,
    setResetFiltersVariables,
    setDefaultVariables,
    setDefaultSearchFilters,
    setResetFilters,
    providerSearchVariables,
    canMakeProviderSearchQuery,
    sectionType,
    acceptingNewPatients,
    psxSource,
    isDeeplink,
    isDeeplinkAutoSearchRadiusQueryDone,
    recommendationPreferences,
  } = useProviderSearchVariables(params);

  const [practitionersResultV2, getPractitionersResultV2] =
    shouldUseProviderSearchPins(isMapLocationsOnly);

  const providerSearchVariablesForAutoSearchRadiusQueryRef = useRef(
    providerSearchVariablesForAutoSearchRadiusQuery
  );
  useEffect(() => {
    // This useEffect is not required for refactored provider Search flow which contains psxSource in the U
    if (psxSource) {
      return;
    }
    if (
      (canMakeAutoSearchRadiusProviderSearchQuery ||
        shouldRunQuery(isDeeplink, isDeeplinkAutoSearchRadiusQueryDone)) &&
      providerSearchVariablesForAutoSearchRadiusQuery.pageSize !== 500
    ) {
      if (
        isFirstRender.current &&
        isDeeplink &&
        isDeeplinkAutoSearchRadiusQueryDone
      ) {
        isFirstRender.current = false; // Set to false after the first render
      } else {
        if (
          !isFirstRender.current &&
          JSON.stringify(
            providerSearchVariablesForAutoSearchRadiusQueryRef.current
          ) === JSON.stringify(providerSearchVariablesForAutoSearchRadiusQuery)
        ) {
          return;
        }
        isFirstRender.current = false; // Set to false after the first render
        // This block will only run when `dependency` changes after the first render
        // This is required to avoid the initial auto search radius query which is not required
        setUpdateAutoSearchRadiusQueryStatus(false);
        getPractitionersResultV2({
          variables: providerSearchVariablesForAutoSearchRadiusQuery,
        });
        setResetFiltersVariables(
          providerSearchVariablesForAutoSearchRadiusQuery
        );
        setDefaultVariables(providerSearchVariablesForAutoSearchRadiusQuery);
        setResetFilters(true);
      }
    }
    // This useffect is to query provider search with autoIncrementRadius.
    // dependency array includes the variables that need to trigger provider search with autoIncrementRadius.
    // ex. location, memberDemographics, locale
  }, [
    canMakeAutoSearchRadiusProviderSearchQuery,
    isDeeplinkAutoSearchRadiusQueryDone,
    isDeeplink,
    providerSearchVariablesForAutoSearchRadiusQuery.locale,
    providerSearchVariablesForAutoSearchRadiusQuery.latitude,
    providerSearchVariablesForAutoSearchRadiusQuery.longitude,
    providerSearchVariablesForAutoSearchRadiusQuery.memberDemographics.gender,
    providerSearchVariablesForAutoSearchRadiusQuery.memberDemographics
      .dateOfBirth,
  ]);

  useEffect(() => {
    if (canMakeProviderSearchQuery) {
      getPractitionersResult({
        variables: providerSearchVariables,
      });
    }
  }, [JSON.stringify(providerSearchVariables), canMakeProviderSearchQuery]);

  const {
    results,
    totalResultsCount,
    providerSearchRadius,
    isLoading,
    error,
    uniqueSearch,
  } = parseProviderResults(sectionType, practitionersResult);

  const {
    providerSearchRadius: autoSearchRadius,
    isLoading: isAutoSearchRadiusQueryLoading,
    totalResultsCount: totalResultsCountFromAutoSearchRadiusQuery,
  } = parseProviderResults(sectionType, practitionersResultV2);

  // if totalResultsCountFromAutoSearchRadiusQuery has results, then totalResultsCount should have results
  // hence keep the loading state to true until totalResultsCount is available.
  const isResultsLoading = totalResultsCountFromAutoSearchRadiusQuery
    ? !totalResultsCount
    : false;

  useEffect(() => {
    if (psxSource) {
      setUpdateAutoSearchRadiusQueryStatus(true);
      return;
    }
    if (autoSearchRadius) {
      setDefaultSearchFilters(
        [
          FilterCollectionModel.AUTO_SEARCH_RADIUS,
          FilterCollectionModel.DISTANCE,
          ...(acceptingNewPatients
            ? [FilterCollectionModel.ACCEPTING_NEW_PATIENTS]
            : []),
        ],
        {
          autoSearchRadius: Number(autoSearchRadius),
          SortBy: searchFilters?.SortBy,
        }
      );
      setUpdateAutoSearchRadiusQueryStatus(true);
    }
  }, [autoSearchRadius, psxSource]);

  useEffect(() => {
    if (updateAutoSearchRadiusQueryStatus && isDeeplink) {
      setUrlParams(
        (urlParams) => {
          urlParams.set('isDeeplinkAutoSearchRadiusQueryDone', 'true');
          return urlParams;
        },
        { replace: true }
      );
      setUpdateAutoSearchRadiusQueryStatus(false);
    }
  }, [updateAutoSearchRadiusQueryStatus, isDeeplink]);

  // When the uniqueSearch value from the response differs from
  // providerSearchVariables.uniqueSearch, it indicates that the provider
  // search parameters have changed and a new query is running.
  // Therefore, set the state to loading.
  const isQueryLoading = canMakeProviderSearchQuery
    ? providerSearchVariables.uniqueSearch !== uniqueSearch
    : false;

  return {
    isLoading:
      isLoading ||
      isAutoSearchRadiusQueryLoading ||
      isResultsLoading ||
      isQueryLoading,
    results,
    error,
    totalResultsCount,
    searchRadius: providerSearchRadius,
    isTieredPlan: practitionersResult?.data?.isTieredPlan,
    headers: practitionersResult?.headers,
    recommendationPreferences,
    uniqueSearch,
  };
};

export const useProviderSummaryVars = (
  isOHBS: boolean,
  providerId?: string,
  locationId?: string
) => {
  const { longitude, latitude, stateCode } = useGeoLocationStorage();
  const { coverageType, storeLob } = useChipStore(
    useShallow((state: ChipState) => ({
      coverageType: state.coverageType,
      storeLob: state.lob,
    }))
  );
  const isSmartChoice = useSmartChoice();
  const currentMember = getCurrentMember();
  const isPreEffective = useCurrentMemberIsPreEffective();
  const tierBenefits = useLagoon('tier-benefit')();
  const featureFlags: [{ key: string; active: boolean }] = useLagoon(
    Constants.LAGOON_TABLE.FEATURE_FLAGS
  )();
  const [uspTierOneFlag, uspToggleFlag] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.USP_TIER1_ENABLE,
    ConstantsLagoon.FEATURE_FLAGS.USP_ENABLE,
  ]);
  const { portalData } = useContext(PortalContext);

  const medicalCoverage = getMemberHealthCoverageType(
    currentMember?.eligibility,
    CoverageTypesCodes.MEDICAL
  );
  const productCode = medicalCoverage?.productCode;
  const marketType = medicalCoverage?.marketType;

  const { membershipCategory, rulesPackageKey } = memberCatRPK(
    currentMember,
    uspTierOneFlag,
    uspToggleFlag,
    tierBenefits,
    ''
  );
  const currentMemberEffectiveDate = getCoverageEffDateRange(
    currentMember,
    coverageType
  ).startDate;

  const recommendationPreferences = getRecommendationPayload();
  const coverages = getCoverageTypes(currentMember);
  const planVariationCode = getPlanVariationCode(currentMember, coverageType);
  const providerType = '';
  const reciprocityId = isOHBS
    ? '100'
    : getNetworkIdsForPES(currentMember, coverageType, featureFlags);

  return {
    longitude,
    latitude,
    stateCode,
    providerType,
    providerId: providerId || null,
    locationId: locationId || null,
    lob: getLob(currentMember?.lineOfBusiness, storeLob),
    population: currentMember?.population,
    membershipCategory,
    coverages,
    situsState: getSitusState(
      currentMember,
      getLob(currentMember?.lineOfBusiness, storeLob)
    ),
    planYear: getMemberPlanYear(isPreEffective, currentMemberEffectiveDate),
    memberEffectiveDate: isPreEffective ? currentMemberEffectiveDate : null,
    planVariationCode,
    claimSystemTypeCode: getClaimOrEligibilitySystemTypeCode(
      currentMember,
      coverageType
    )?.claimSystemTypeCode,
    eligibilitySystemTypeCode: getClaimOrEligibilitySystemTypeCode(
      currentMember,
      coverageType
    )?.eligibilitySystemTypeCode,
    policyID: currentMember?.policyNumber,
    reciprocityId,
    isProviderRecommendationsEnabled: isSmartChoice,
    recommendationPreferences,
    rulesPackageKey,
    snpType:
      coverageType == ReverseCoverageTypesCodes.MEDICAL
        ? medicalCoverage?.snpType
        : '',
    coverageType,
    locale: getLocale(),
    ddpCode: currentMember?.ddpCode,
    cesProductCode: productCode,
    memberMarketType: marketType,
    isGuestUser: portalData?.portalConfig?.isGuestUser,
  };
};

export const useProviderSummaryResult = ({ providerId, isOHBS }) => {
  const { portalData } = useContext(PortalContext);
  const currentMember = useCurrentMember();
  const isUmrPopulation = isUmrMember(portalData?.portalName);
  const [providerResult, getProviderResult] = useProviderSearchV2({});

  const {
    isIFPEarlyPay,
    lineOfBusiness,
    policyNumber: policyID,
    population,
  } = currentMember || {};

  const variables = useProviderSummaryVars(isOHBS, providerId);

  const isGuestUser = portalData?.portalConfig?.isGuestUser;

  const isEniSsoMemberPopulation = isEniSsoMember(portalData?.portalName);

  const validPlanVariationCode = isValidPlanVariationCode(
    variables.eligibilitySystemTypeCode,
    variables.claimSystemTypeCode,
    variables.planVariationCode,
    variables.lob,
    variables.population,
    isUmrPopulation,
    isIFPEarlyPay,
    isGuestUser,
    isEniSsoMemberPopulation
  );

  const planVariationCodeRequired = isPlanVariationCodeRequired(
    lineOfBusiness,
    population,
    portalData?.portalName,
    isIFPEarlyPay,
    isGuestUser
  );

  const isPolicyIDRequired = getIsPolicyIDRequired(currentMember, isGuestUser);

  const requiredParams = {
    coverages: variables.coverages,
    reciprocityId: variables.reciprocityId,
    ...(isPolicyIDRequired ? { policyID } : {}),
    latitude: variables.latitude,
    longitude: variables.longitude,
    coverageType: variables.coverageType,
    ...(planVariationCodeRequired
      ? { planVariationCode: variables.planVariationCode }
      : {}),
  };

  const isMissingRquiredParameters = isMissingParameters(
    'Provider Search',
    requiredParams
  );

  useEffect(() => {
    if (providerId) {
      if (!validPlanVariationCode) {
        throw new Error('Plan Variation Code is required');
      }

      if (isMissingRquiredParameters) {
        return;
      }
      getProviderResult({
        variables,
      });
    }
  }, [
    providerId,
    getProviderResult,
    validPlanVariationCode,
    isMissingRquiredParameters,
  ]);

  return providerResult;
};

export const useProviderSearchPESV2 = (options) => {
  const [, setCorrelationIDSessionStorage] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.CORRELATION_ID_HEADER,
    ''
  );
  const [queryResult, queryAction] = useCustomQuery(
    GET_PROVIDER_SEARCH_V2_QUERY,
    {
      ...options,
      url: config('GRAPHQL_API_URL'),
      accessor: 'providerSearchV2',
      onCompleted: (result) => {
        const data = filterSearchResultsV2(result?.data);
        const headers = {
          correlationId: result.headers[OPTUM_CORRELATION_ID_HEADER],
        };
        result?.headers[OPTUM_CORRELATION_ID_HEADER] &&
          setCorrelationIDSessionStorage(
            result?.headers[OPTUM_CORRELATION_ID_HEADER]
          );
        if (options?.onCompleted) {
          options.onCompleted({ ...data, headers });
        }
      },
    }
  );

  const parsedResult = useMemo(() => {
    const isLoading = queryResult.loading;
    const data = queryResult.data
      ? filterSearchResultsV2(queryResult.data)
      : {};
    const headers = queryResult.headers
      ? { correlationId: queryResult.headers[OPTUM_CORRELATION_ID_HEADER] }
      : {};

    return {
      ...queryResult,
      isLoading,
      data,
      headers,
    };
  }, [
    JSON.stringify(queryResult.loading),
    JSON.stringify(queryResult.data),
    JSON.stringify(queryResult.headers),
    JSON.stringify(options),
  ]);

  return [parsedResult, queryAction];
};

export const useProviderSearchVariablesV2 = (params) => {
  const baseVariables = useProviderSearchVariables(params);
  const currentMember = useCurrentMemberOrDependentInfo(params.dependentSeqNbr);
  const featureFlags = useLagoon('feature-flags')();
  const { coverageType: coverageTypeFromStore, chipValue: chipValueFromStore } =
    useChipStore(
      useShallow((state: ChipState) => ({
        coverageType: state.coverageType,
        chipValue: state.chipValue,
      }))
    );
  const { pcpIndicator = false } = usePCPStore(
    useShallow((state: PCPSearchStore) => ({
      pcpIndicator: state.pcpSearchState.pcpIndicator,
    }))
  );

  const [urlParams] = useSearchParams();
  const searchParamsObject = Object.fromEntries(urlParams.entries());
  const {
    coverageType: coverageTypeFromParams,
    chipValue: chipValueFromParams,
    [Constants.SOURCE]: sourceFromGlobalSearch,
    [Constants.PSX_SOURCE]: psxSource,
  } = searchParamsObject;

  const coverageType = coverageTypeFromParams || coverageTypeFromStore;
  const chipValue = chipValueFromParams || chipValueFromStore;
  const isPCPLandingPage = resolveIsPCPLandingPage(
    chipValue,
    psxSource,
    sourceFromGlobalSearch
  );

  const coverageTypes = buildCoverageTypesArray(
    currentMember,
    featureFlags,
    coverageType
  );

  const providerTypes = buildProviderTypesArray({
    searchAllTypes: params.searchAllTypes,
    providerType: baseVariables.providerType,
    sectionType: baseVariables.sectionType,
    chipValue,
    isCommonProviderSearches: params.isCommonProviderSearches,
  });

  const roleCodes = mapProviderTypesToRoleCodes(
    providerTypes,
    pcpIndicator,
    isPCPLandingPage || false
  );

  const searchNames = getSearchNameForV2(
    baseVariables.variables.search,
    baseVariables.variables.searchType
  );

  const pesVersion = params.pesVersion;

  if (!pesVersion) {
    throw new Error(
      'pesVersion is required for V2 endpoint. Please provide pesVersion: "1" or pesVersion: "2"'
    );
  }

  const isPesV2 = pesVersion === '2';

  if (isPesV2) {
    if (!baseVariables.providerSearchVariables.sortBy) {
      throw new Error(
        'sortBy is required for pesVersion "2". Please provide a valid sort option (e.g., "ACQ_SORT", "distance").'
      );
    }
  }

  const v2Variables = {
    ...baseVariables.providerSearchVariables,
    pesVersion,
    coverageTypes,
    providerTypes,
    roleCodes,
    ...searchNames,
    coverages: undefined,
    coverageType: undefined,
    providerType: undefined,
    reciprocityId: undefined,
    searchType: undefined,
    useMedicalNewRollUpCodes: undefined,
    useBHNewRollUpCodes: undefined,
    useVisionNewRollUpCodes: undefined,
    useDentalNewRollUpCodes: undefined,
  };

  if (!isPesV2) {
    if (!baseVariables.validPlanVariationCode) {
      throw new Error('Plan Variation Code is required for pesVersion "1"');
    }
    if (!baseVariables.providerSearchVariables.policyID) {
      throw new Error('policyID is required for pesVersion "1"');
    }
  }

  return {
    ...baseVariables,
    providerSearchVariables: v2Variables,
    coverageTypes,
    providerTypes,
    roleCodes,
    isPesV2,
  };
};

export const useFilterProviderResultsPESV2 = (params) => {
  const [, setUrlParams] = useSearchParams();
  const { searchFilters } = useContext(SearchFilterContext);
  const isFirstRender = useRef(true);
  const [
    updateAutoSearchRadiusQueryStatus,
    setUpdateAutoSearchRadiusQueryStatus,
  ] = useState(false);
  const [practitionersResult, getPractitionersResult] = useProviderSearchPESV2(
    {}
  );
  const variablesData = useProviderSearchVariablesV2(params);

  const {
    canMakeAutoSearchRadiusProviderSearchQuery,
    providerSearchVariablesForAutoSearchRadiusQuery,
    setResetFiltersVariables,
    setDefaultVariables,
    setDefaultSearchFilters,
    setResetFilters,
    providerSearchVariables,
    canMakeProviderSearchQuery,
    sectionType,
    acceptingNewPatients,
    psxSource,
    isDeeplink,
    isDeeplinkAutoSearchRadiusQueryDone,
    recommendationPreferences,
  } = variablesData;

  const [practitionersResultV2, getPractitionersResultV2] =
    useProviderSearchPESV2({});

  const providerSearchVariablesForAutoSearchRadiusQueryRef = useRef(
    providerSearchVariablesForAutoSearchRadiusQuery
  );

  useEffect(() => {
    if (psxSource) {
      return;
    }
    if (
      (canMakeAutoSearchRadiusProviderSearchQuery ||
        (isDeeplink && !isDeeplinkAutoSearchRadiusQueryDone)) &&
      providerSearchVariablesForAutoSearchRadiusQuery.pageSize !== 500
    ) {
      if (
        isFirstRender.current &&
        isDeeplink &&
        isDeeplinkAutoSearchRadiusQueryDone
      ) {
        isFirstRender.current = false;
      } else {
        if (
          !isFirstRender.current &&
          JSON.stringify(
            providerSearchVariablesForAutoSearchRadiusQueryRef.current
          ) === JSON.stringify(providerSearchVariablesForAutoSearchRadiusQuery)
        ) {
          return;
        }
        isFirstRender.current = false;
        setUpdateAutoSearchRadiusQueryStatus(false);
        getPractitionersResultV2({
          variables: providerSearchVariablesForAutoSearchRadiusQuery,
        });
        setResetFiltersVariables(
          providerSearchVariablesForAutoSearchRadiusQuery
        );
        setDefaultVariables(providerSearchVariablesForAutoSearchRadiusQuery);
        setResetFilters(true);
      }
    }
  }, [
    canMakeAutoSearchRadiusProviderSearchQuery,
    isDeeplinkAutoSearchRadiusQueryDone,
    isDeeplink,
    providerSearchVariablesForAutoSearchRadiusQuery.locale,
    providerSearchVariablesForAutoSearchRadiusQuery.latitude,
    providerSearchVariablesForAutoSearchRadiusQuery.longitude,
    providerSearchVariablesForAutoSearchRadiusQuery.memberDemographics?.gender,
    providerSearchVariablesForAutoSearchRadiusQuery.memberDemographics
      ?.dateOfBirth,
  ]);

  useEffect(() => {
    if (canMakeProviderSearchQuery) {
      getPractitionersResult({
        variables: providerSearchVariables,
      });
    }
  }, [JSON.stringify(providerSearchVariables), canMakeProviderSearchQuery]);

  const { results, totalResultsCount, providerSearchRadius, isLoading, error } =
    parseProviderResults(sectionType, practitionersResult);

  const {
    providerSearchRadius: autoSearchRadius,
    isLoading: isAutoSearchRadiusQueryLoading,
    totalResultsCount: totalResultsCountFromAutoSearchRadiusQuery,
  } = parseProviderResults(sectionType, practitionersResultV2);

  const isResultsLoading = totalResultsCountFromAutoSearchRadiusQuery
    ? !totalResultsCount
    : false;

  useEffect(() => {
    if (psxSource) {
      setUpdateAutoSearchRadiusQueryStatus(true);
      return;
    }
    if (autoSearchRadius) {
      setDefaultSearchFilters(
        [
          FilterCollectionModel.AUTO_SEARCH_RADIUS,
          FilterCollectionModel.DISTANCE,
          ...(acceptingNewPatients
            ? [FilterCollectionModel.ACCEPTING_NEW_PATIENTS]
            : []),
        ],
        {
          autoSearchRadius: Number(autoSearchRadius),
          SortBy: searchFilters?.SortBy,
        }
      );
      setUpdateAutoSearchRadiusQueryStatus(true);
    }
  }, [autoSearchRadius, psxSource]);

  useEffect(() => {
    if (updateAutoSearchRadiusQueryStatus && isDeeplink) {
      setUrlParams(
        (urlParams) => {
          urlParams.set('isDeeplinkAutoSearchRadiusQueryDone', 'true');
          return urlParams;
        },
        { replace: true }
      );
      setUpdateAutoSearchRadiusQueryStatus(false);
    }
  }, [updateAutoSearchRadiusQueryStatus, isDeeplink]);

  return {
    isLoading: isLoading || isAutoSearchRadiusQueryLoading || isResultsLoading,
    results,
    error,
    totalResultsCount,
    searchRadius: providerSearchRadius,
    isTieredPlan: practitionersResult?.data?.isTieredPlan,
    headers: practitionersResult?.headers,
    recommendationPreferences,
  };
};
